import get from 'lodash.get';
import { CANVAS_WIDTH_DESKTOP, CANVAS_WIDTH_TABLET, MENUBAR_WIDTH } from './catalogConstants';
import { DESKTOP, TABLET, MOBILE } from '../utils/deviceType';
import { PORTRAIT } from './catalogConstants';

export const paperTypeVars = {
    'paper:catalog:contemporary': {
        margin: 36,
        mobileScale: 0.35,
        scale: 0.875,
        singlePageRatio: 0.81, // width/height of a single page
        tabletScale: 0.6,
        thumbnailScale: 0.11,
        zoomedScale: 1.5,
    },
    'paper:catalog:roomsofdistinction': {
        dimension83: 'rooms_of_distinction_placeholder',
        margin: 36,
        mobileScale: 0.35,
        scale: 1.0,
        singlePageRatio: 0.83,
        tabletScale: 0.7,
        thumbnailScale: 0.125,
        zoomedScale: 1.5,
    },
};

export const isSinglePage = (pageNum, pdfPageCount) => {
    return pageNum === 1 || pageNum === pdfPageCount;
};

// calculate total pages where first and last page are singles
// and everything else is a spread of two pages
export const calculateTotalPages = pdfPageCount => {
    const double = pdfPageCount * 2;
    return double - 2;
};

// are we in portrait orientation on a phone
// has several UI differences from other views
export const isMobilePortrait = (deviceType, screenOrientation) => {
    return deviceType === MOBILE && screenOrientation === PORTRAIT;
};

// convert page index in pdf file to a page number that makes sense to customer
// page by page for portrait rather than double pages as a unit
export const calculatedPageNumberSingles = (pdfPageNumber, pdfPageCount, isRightSide) => {
    let currentPage = pdfPageNumber; // default is page 1

    // for phone in portrait mode, full spread pages are spread in two
    // displayed as left and right and so is calculated differently
    if (pdfPageNumber && pdfPageCount) {
        if (pdfPageNumber > 1) {
            const double = pdfPageNumber * 2;
            currentPage = double - 2;

            if (isRightSide) {
                currentPage += 1;
            }
        }

        return currentPage.toString();
    }
    return null;
};

// convert page index in pdf file to a page number that makes sense to customer
// count double pages as a unit (e.g., Page 2-3 / 36)
export const calculatedPageNumberDoubles = (pdfPageNumber, pdfPageCount) => {
    let currentPage = pdfPageNumber; // default is page 1

    if (pdfPageNumber && pdfPageCount) {
        const totalPages = calculateTotalPages(pdfPageCount);

        // for full spread pages count as two pages
        if (pdfPageNumber > 1 && pdfPageNumber < pdfPageCount) {
            const double = pdfPageNumber * 2;
            const firstNum = double - 2;
            currentPage = `${firstNum}-${firstNum + 1}`;
        } else if (pdfPageNumber === pdfPageCount) {
            currentPage = totalPages;
        }

        return currentPage.toString();
    }
    return null;
};

export const calculatedPageNumberText = (
    pdfPageNumber,
    pdfPageCount,
    countAsSingles,
    isRightSide
) => {
    if (pdfPageNumber && pdfPageCount) {
        let currentPage;
        if (countAsSingles) {
            currentPage = calculatedPageNumberSingles(pdfPageNumber, pdfPageCount, isRightSide);
        } else {
            currentPage = calculatedPageNumberDoubles(pdfPageNumber, pdfPageCount);
        }
        return `Page ${currentPage} / ${calculateTotalPages(pdfPageCount)}`;
    }
    return null;
};

export const getScaledPageDimensions = (currentPage, pdfPageCount, singlePageDimensions, scale) => {
    const isSingle = isSinglePage(currentPage, pdfPageCount);
    const height = get(singlePageDimensions, 'height') || 0;
    let width = get(singlePageDimensions, 'width') || 0;

    if (!isSingle) {
        width = width * 2;
    }

    return {
        height: height * scale,
        width: width * scale,
    };
};

export const getCanvasWidth = (deviceType, screenOrientation) => {
    let width = CANVAS_WIDTH_DESKTOP;

    if (deviceType !== DESKTOP) {
        if (deviceType === TABLET) {
            width = CANVAS_WIDTH_TABLET;
        } else if (deviceType === MOBILE) {
            if (isMobilePortrait(deviceType, screenOrientation)) {
                // for portrait mode on phone, set width to be the width of the phone
                width = window.innerWidth;
            } else {
                // for phone in landscape, subtract space for menu bar and 10px on either side padding
                width = window.innerWidth - MENUBAR_WIDTH - 36;
            }
        }
    }
    return width;
};

export const getCanvasDimensions = (
    singlePageDimensions,
    scale,
    margin,
    deviceType = DESKTOP,
    screenOrientation,
    ratio
) => {
    const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);
    const width = getCanvasWidth(deviceType, screenOrientation);
    const pageHeight = get(singlePageDimensions, 'height') || 0;
    let scaledPageHeight = pageHeight * scale;

    if (isPhonePortrait) {
        scaledPageHeight = window.innerWidth / ratio;
    }

    // make sure height of canvas includes space for top and bottom margins
    return {
        height: scaledPageHeight ? Math.ceil(scaledPageHeight) + margin + margin : null,
        width,
    };
};

export const buildLink = (linkType, hotspotKey) => {
    switch (linkType) {
        case 'DEALER':
            return `/dealers/${hotspotKey}/`;
        case 'DIRECT_LINK':
            return hotspotKey;
        case 'SATURDAY_SHOPPER':
            return `/shopping-with/${hotspotKey}/`;
        case 'DESIGN_PROJECT':
            return `/project/${hotspotKey}/`;
        case '1STDIBS_LINK':
            return `/${hotspotKey}/`;
        case 'ITEM':
        default:
            return `/id-${hotspotKey}/`;
    }
};

const truncatedLengthDefault = 190;
const truncatedTailDefault = '...';
const indexesToCheckForSpace = 5;
export const truncateItemDescription = ({
    itemDescription = '',
    truncatedLength = truncatedLengthDefault,
    tail = truncatedTailDefault,
}) => {
    if (typeof itemDescription !== 'string' || !itemDescription) {
        return '';
    }

    // finite real integer
    if (!Number.isInteger(truncatedLength) || truncatedLength < 0) {
        return itemDescription;
    }

    if (itemDescription.length <= truncatedLength) {
        return itemDescription;
    }

    const checkedTail = typeof tail !== 'string' || !tail ? truncatedTailDefault : tail;

    const truncatedDescription = itemDescription.substr(0, truncatedLength);

    // look for a space within the last five characters
    // remove the space and the characters to the right
    let spaceCheckedTruncatedDescription = null;

    for (let i = truncatedLength - 1; i > truncatedLengthDefault - indexesToCheckForSpace; i--) {
        if (truncatedDescription[i] === ' ') {
            spaceCheckedTruncatedDescription = truncatedDescription.substr(0, i);
            break;
        }
    }

    return (spaceCheckedTruncatedDescription || truncatedDescription) + checkedTail;
};
